/** Immutable graph edits shared by drag/drop and tests. Invalid moves are no-ops. */
export const findNodeParent = (nodes, childId) => {
  let parent = null;
  nodes.some((node) => Object.entries(node.slots || {}).some(([slotKey, children]) => {
    if (!Array.isArray(children) || !children.includes(childId)) return false;
    parent = { parentId: node.id, slotKey, children };
    return true;
  }));
  return parent;
};

export const collectDescendants = (nodes, nodeId, visited = new Set()) => {
  if (visited.has(nodeId)) return visited;
  visited.add(nodeId);
  const node = nodes.find((item) => item.id === nodeId);
  Object.values(node?.slots || {}).forEach((children) => {
    if (Array.isArray(children)) children.forEach((id) => collectDescendants(nodes, id, visited));
  });
  return visited;
};

const cloneNodes = (nodes) => nodes.map((node) => ({
  ...node,
  ...(node.slots ? { slots: Object.fromEntries(Object.entries(node.slots).map(([key, value]) => [
    key, Array.isArray(value) ? [...value] : value,
  ])) } : {}),
}));

export const removeNode = (nodes, rootId, nodeId) => {
  if (nodeId === rootId || !nodes.some((node) => node.id === nodeId)) return nodes;
  const removed = collectDescendants(nodes, nodeId);
  if (removed.has(rootId)) return nodes;
  return cloneNodes(nodes).filter((node) => !removed.has(node.id)).map((node) => ({
    ...node,
    ...(node.slots ? { slots: Object.fromEntries(Object.entries(node.slots).map(([key, children]) => [
      key, Array.isArray(children) ? children.filter((id) => !removed.has(id)) : children,
    ])) } : {}),
  }));
};

export const insertNode = (nodes, rootId, dropPosition, nodeOrId) => {
  const moving = typeof nodeOrId === 'string';
  const nodeId = moving ? nodeOrId : nodeOrId?.id;
  if (!nodeId || nodeId === rootId || !dropPosition) return nodes;
  if (moving && !nodes.some((node) => node.id === nodeId)) return nodes;
  if (!moving && nodes.some((node) => node.id === nodeId)) return nodes;
  if (dropPosition.nodeId === nodeId) return nodes;

  const inside = dropPosition.position === 'inside';
  if (!inside && !['before', 'after'].includes(dropPosition.position)) return nodes;
  const parentInfo = inside ? null : findNodeParent(nodes, dropPosition.nodeId);
  const parentId = inside ? dropPosition.nodeId : parentInfo?.parentId;
  const parent = nodes.find((node) => node.id === parentId);
  const slotKey = inside
    ? (dropPosition.slotKey || Object.keys(parent?.slots || {}).find((key) => Array.isArray(parent.slots[key])))
    : parentInfo?.slotKey;
  if (!Array.isArray(parent?.slots?.[slotKey])) return nodes;
  if (moving && collectDescendants(nodes, nodeId).has(parentId)) return nodes;

  const updated = cloneNodes(nodes);
  updated.forEach((node) => {
    Object.entries(node.slots || {}).forEach(([key, children]) => {
      if (Array.isArray(children)) node.slots[key] = children.filter((id) => id !== nodeId);
    });
  });
  if (!moving) updated.push(nodeOrId);
  const target = updated.find((node) => node.id === parentId).slots[slotKey];
  // Compute relative position after removal so moves within one slot stay correct.
  const index = inside ? target.length : target.indexOf(dropPosition.nodeId) + (dropPosition.position === 'after' ? 1 : 0);
  target.splice(index, 0, nodeId);
  return updated;
};

export const createCanvasNode = (type, id, descriptor = {}) => {
  const props = {};
  Object.entries(descriptor.properties || {}).forEach(([key, definition]) => {
    const value = definition.default !== undefined ? definition.default : definition.def;
    if (value !== undefined) props[key] = typeof value === 'function' ? value() : value;
  });
  const slots = Array.isArray(descriptor.slots) ? descriptor.slots : Object.keys(descriptor.slots || {});
  const fallbackSlots = type === 'columns'
    ? Array.from({ length: Math.max(1, Number(props.columns) || 2) }, (_, index) => `col-${index + 1}`)
    : (descriptor.category === 'container' ? ['items'] : []);
  const slotNames = slots.length ? slots : fallbackSlots;
  return {
    id,
    type,
    props,
    ...(slotNames.length ? { slots: Object.fromEntries(slotNames.map((key) => [key, []])) } : {}),
  };
};

/** Duplicate one subtree beside its source, preserving every named slot. */
export const duplicateNode = (nodes, rootId, nodeId, makeId) => {
  if (nodeId === rootId || typeof makeId !== 'function') return nodes;
  const parent = findNodeParent(nodes, nodeId);
  if (!parent) return nodes;
  const subtree = collectDescendants(nodes, nodeId);
  if (subtree.has(rootId)) return nodes;
  const sourceNodes = nodes.filter((node) => subtree.has(node.id));
  if (sourceNodes.length !== subtree.size) return nodes;

  const usedIds = new Set(nodes.map((node) => node.id));
  const newIds = new Map();
  const idsValid = sourceNodes.every((node) => {
    const id = makeId(node.type);
    if (typeof id !== 'string' || !id || usedIds.has(id)) return false;
    usedIds.add(id);
    newIds.set(node.id, id);
    return true;
  });
  if (!idsValid) return nodes;

  const copies = sourceNodes.map((node) => {
    const copy = JSON.parse(JSON.stringify(node));
    copy.id = newIds.get(node.id);
    Object.entries(copy.slots || {}).forEach(([key, children]) => {
      if (Array.isArray(children)) copy.slots[key] = children.map((id) => newIds.get(id));
    });
    return copy;
  });
  const copiedRootId = newIds.get(nodeId);
  const copiedRoot = copies.find((node) => node.id === copiedRootId);
  const descendants = copies.filter((node) => node.id !== copiedRootId);
  return insertNode([...nodes, ...descendants], rootId, { nodeId, position: 'after' }, copiedRoot);
};
